/**
 * AddWorkOrderPartyDialog — pick a party for one role during work order
 * creation (#1108).
 *
 * Unlike WorkOrderPartyDialog (detail-page editing), the work order doesn't
 * exist yet here — the picked party is held in local wizard state via
 * `onAdd` and persisted only after the work order is created.
 */

import { useTranslation } from 'react-i18next';
import { DialogContent } from '@mui/material';
import { FormDialog } from '@/components/common/FormDialog';
import { PartyPicker, type PartyRef } from '@/components/parties/PartyPicker';
import { allowedPartyTypesForRole, type WorkOrderPartyRole } from '@/lib/workOrderPartyRoles';

interface AddWorkOrderPartyDialogProps {
  open: boolean;
  role: WorkOrderPartyRole;
  onClose: () => void;
  onAdd: (role: WorkOrderPartyRole, party: PartyRef) => void;
}

export function AddWorkOrderPartyDialog({ open, role, onClose, onAdd }: AddWorkOrderPartyDialogProps) {
  const { t } = useTranslation('workorders');

  return (
    <FormDialog open={open} onClose={onClose} title={t(`parties.roles.${role}`)} isDirty={false} maxWidth="sm">
      <DialogContent>
        {open && (
          <PartyPicker
            key={role}
            allowedTypes={allowedPartyTypesForRole(role)}
            autoFocus
            onSelect={(party) => {
              onAdd(role, party);
              onClose();
            }}
          />
        )}
      </DialogContent>
    </FormDialog>
  );
}
